import { useEffect, useState, type ReactNode } from 'react';
import { AuthContext, type AuthUser, type AuthContextValue } from './AuthContext';
import { useLanguage, type Locale } from '../i18n';

export function AuthProvider({ children }: { children: ReactNode }) {
  const { locale, setLocale } = useLanguage();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUser = async () => {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'same-origin' });
      if (response.ok) {
        const data = (await response.json()) as { user: AuthUser };
        setUser(data.user);
        if (data.user.locale) {
          setLocale(data.user.locale);
        }
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const handleAuthResponse = async (response: Response) => {
    const data = (await response.json()) as { user?: AuthUser; error?: string };
    if (!response.ok) {
      throw new Error(data.error || `Request failed: ${response.status}`);
    }
    if (data.user) {
      setUser(data.user);
      if (data.user.locale) {
        setLocale(data.user.locale);
      }
    }
  };

  const login = async (email: string, password: string) => {
    setError(null);
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ email, password }),
    });
    await handleAuthResponse(response);
  };

  const register = async (email: string, password: string, plan?: AuthUser['plan']) => {
    setError(null);
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ email, password, plan, locale: locale || 'en' }),
    });
    await handleAuthResponse(response);
  };

  const updateLocale = async (next: Locale) => {
    if (!user) return;
    setError(null);
    try {
      const response = await fetch('/api/auth/locale', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ locale: next }),
      });
      if (response.ok) {
        const data = (await response.json()) as { user?: AuthUser };
        if (data.user) {
          setUser(data.user);
        }
      }
    } catch (err) {
      // Local storage already updated by LanguageProvider; backend sync is best-effort.
      console.error('Failed to sync locale:', err);
    }
  };

  const logout = async () => {
    setError(null);
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
    });
    setUser(null);
  };

  const upgradePlan = async (nextPlan: AuthUser['plan']) => {
    setError(null);
    const response = await fetch('/api/auth/plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ plan: nextPlan }),
    });
    const data = (await response.json()) as { user?: AuthUser; error?: string };
    if (!response.ok) {
      throw new Error(data.error || `Request failed: ${response.status}`);
    }
    if (data.user) {
      setUser(data.user);
    }
  };

  const value: AuthContextValue = {
    user,
    loading,
    error,
    isAdmin: user?.role === 'admin',
    login,
    register,
    logout,
    refreshUser,
    updateLocale,
    upgradePlan,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export type { AuthUser };
